import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { classMakeupApi } from "./api";
import type { MakeupRecord } from "./types";
import { formatDate, formatTime, statusChip } from "./ui";

type Props = {
  recordKey: string | null;
  fromDate?: string;
  toDate?: string;
  onClose: () => void;
};

export default function MakeupPreviewDrawer({ recordKey, fromDate, toDate, onClose }: Props) {
  const [loading, setLoading] = useState(false);
  const [record, setRecord] = useState<MakeupRecord | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!recordKey) return;
    setLoading(true);
    setError(null);
    classMakeupApi
      .previewOne(recordKey, fromDate, toDate)
      .then((r) => setRecord(r.record))
      .catch((e) => setError(e.message || String(e)))
      .finally(() => setLoading(false));
  }, [recordKey, fromDate, toDate]);

  return (
    <Drawer anchor="right" open={!!recordKey} onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", md: 720 } } }}>
      <Box p={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6">Makeup Class Details</Typography>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Stack>

        {loading && <Box textAlign="center" py={4}><CircularProgress /></Box>}
        {error && <Alert severity="error">{error}</Alert>}

        {record && (
          <>
            <Box mb={2}>{statusChip(record.status)}</Box>

            {record.warnings.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {record.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </Alert>
            )}

            <Typography variant="subtitle2" gutterBottom>Original missed class</Typography>
            <Grid container spacing={1} sx={{ mb: 2, pl: 1 }}>
              <Info label="Legacy day ID" value={record.legacy_original_day_id} />
              <Info label="Date" value={formatDate(record.original_date)} />
              <Info label="Time" value={`${formatTime(record.original_from_time)} – ${formatTime(record.original_to_time)}`} />
              <Info label="Staff" value={record.original_staff_name} />
              <Info label="Student" value={record.student_name} />
              <Info label="Legacy student ID" value={record.legacy_student_id} />
              <Info label="Subjects" value={record.student_subject_ids || record.original_grade_subject || "—"} />
              <Info label="Released?" value={record.original_released ? "Yes" : "No"} />
              <Info label="Cancelled?" value={record.original_cancel ? "Yes" : "No"} />
              {record.memo && <Info label="Memo" value={String(record.memo)} full />}
              {record.messages && <Info label="Message" value={String(record.messages)} full />}
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>Makeup class</Typography>
            {record.makeup_row_missing ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                Referenced legacy day_details row is missing (dayid={record.legacy_makeup_day_id || "—"}).
              </Alert>
            ) : (
              <Grid container spacing={1} sx={{ mb: 2, pl: 1 }}>
                <Info label="Makeup legacy day ID" value={record.legacy_makeup_day_id} />
                <Info label="Date" value={formatDate(record.makeup_date)} />
                <Info label="Time" value={`${formatTime(record.makeup_start_time)} – ${formatTime(record.makeup_end_time)}`} />
                <Info label="Staff" value={record.makeup_staff_name} />
                <Info label="Staff email" value={record.makeup_staff_email} />
                <Info label="Location" value={record.location_name} />
                <Info label="Mode" value={record.study_mode || "—"} />
                <Info label="Capacity" value={record.makeup_capacity || "—"} />
                <Info label="Roster in makeup row" value={record.makeup_row_details?.length ?? 0} />
              </Grid>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>Mapping</Typography>
            <Grid container spacing={1} sx={{ mb: 2, pl: 1 }}>
              <Info label="Legacy student → Live" value={record.live_student_id ? `#${record.live_student_id}` : <Chip size="small" color="error" label="Unmapped" />} />
              <Info label="Legacy makeup staff → Live" value={record.live_makeup_staff_id ? `#${record.live_makeup_staff_id}` : <Chip size="small" color="error" label="Unmapped" />} />
              <Info label="Legacy original staff → Live" value={record.live_original_staff_id ? `#${record.live_original_staff_id}` : "—"} />
              <Info label="Grade/subject pivots" value={record.subject_pivot_ids?.length ? record.subject_pivot_ids.join(", ") : <Chip size="small" color="error" label="None" />} />
              <Info label="Location" value={record.location_name || <Chip size="small" label="Unassigned" />} />
              <Info label="Original live occurrence" value={record.live_original_occurrence_id ? `#${record.live_original_occurrence_id}` : "—"} />
              <Info label="Existing makeup" value={record.existing_makeup_id ? `#${record.existing_makeup_id}` : "—"} />
            </Grid>

            {(record.conflicts?.staff?.length || record.conflicts?.student?.length) ? (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>Conflicts</Typography>
                {!!record.conflicts?.staff?.length && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    Staff has {record.conflicts.staff.length} overlapping session(s).
                  </Alert>
                )}
                {!!record.conflicts?.student?.length && (
                  <Alert severity="warning">
                    Student has {record.conflicts.student.length} overlapping session(s).
                  </Alert>
                )}
              </>
            ) : null}
          </>
        )}
      </Box>
    </Drawer>
  );
}

function Info({ label, value, full }: { label: string; value: React.ReactNode; full?: boolean }) {
  return (
    <Grid item xs={full ? 12 : 6}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="body2">{value ?? "—"}</Typography>
    </Grid>
  );
}
